import { useEffect, useState } from 'react'
import { Check, ChevronRight, Pencil, Power, X } from 'lucide-react'
import type { GroupDetail, GroupStatus } from '@shared/groups'
import { workersApi } from '../../lib/api'
import { useWorkerGroup } from '../../hooks/useWorker'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'
import { Metric, Section, StatusPill, WorkerBody, usd } from './WorkerCard'

/**
 * Several workers on ONE approval card. Every number comes from the members' compiled plans; one
 * Approve places every member at IBKR (or none), one Stop closes them all.
 */

export const GROUP_STATUS_LABEL: Record<GroupStatus, string> = {
  draft: 'Awaiting approval',
  rejected: 'Rejected',
  expired: 'Expired',
  active: 'Running',
  done: 'Done',
  cancelled: 'Cancelled',
  error: 'Did not start',
}

export function GroupPill({ status }: { status: GroupStatus }) {
  const tone = status === 'active' ? 'live' : status === 'draft' ? 'wait' : status === 'error' ? 'bad' : 'off'
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-inset)] px-2 py-0.5 text-[10.5px] font-medium',
        tone === 'bad' ? 'text-down' : tone === 'off' ? 'text-fg-faint' : 'text-fg-muted',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', tone === 'live' ? 'bg-up' : tone === 'wait' ? 'animate-pulse bg-fg-muted' : tone === 'bad' ? 'bg-down' : 'bg-flat')} />
      {GROUP_STATUS_LABEL[status]}
    </span>
  )
}

const btn = 'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-colors disabled:opacity-40'
const when = (t: number) => new Date(t).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

function GroupBody({ g }: { g: GroupDetail }) {
  const [open, setOpen] = useState<string | null>(null)
  const s = g.summary
  const rt = g.runtime
  const draft = g.status === 'draft'
  const fired = new Set(rt?.firedRules ?? [])
  const blocking = g.members.flatMap((w) => w.checks.filter((c) => !c.ok && c.level !== 'info').map((c) => ({ w, c })))
  const realized = g.members.reduce((a, w) => a + (w.runtime?.realized ?? 0), 0)
  const fees = g.members.reduce((a, w) => a + (w.runtime?.commissions ?? 0), 0)
  return (
    <>
      <div className="grid grid-cols-2 gap-1.5 px-3.5 pb-2.5 sm:grid-cols-4">
        <Metric label="Max loss · after fees" value={usd(-s.maxLoss)} tone="down" sub={g.spec.oneCancelsOthers ? 'worst single member' : 'every member at its stops'} />
        <Metric label="Members" value={String(g.members.length)} sub={g.members.map((w) => w.symbol).join(' · ')} />
        <Metric label="Linked by" value={g.spec.oneCancelsOthers ? 'One of' : g.spec.rules.length ? 'Rules' : 'Start order'} sub={`${g.spec.rules.length} group rule${g.spec.rules.length === 1 ? '' : 's'}`} />
        {draft || rt?.pnl == null ? (
          <Metric label="Realized" value={draft ? '—' : usd(realized, true)} tone={realized > 0 ? 'up' : realized < 0 ? 'down' : undefined} sub={fees > 0 ? `${usd(fees)} IBKR fees` : undefined} />
        ) : (
          <Metric
            label="Combined P&L"
            value={usd(rt.pnl, true)}
            tone={rt.pnl > 0 ? 'up' : rt.pnl < 0 ? 'down' : undefined}
            sub={rt.pnlAt ? `at broker prices · ${new Date(rt.pnlAt).toLocaleTimeString()}` : undefined}
          />
        )}
      </div>
      <div className="px-3.5 pb-2.5 text-[11.5px] text-fg-subtle">{s.maxLossText}</div>

      <Section title="How they work together">
        <ul className="space-y-1 text-[12px] text-fg-muted">
          {s.behaviour.map((t, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="mt-[6px] h-1 w-1 shrink-0 rounded-full bg-[var(--line)]" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </Section>

      {s.rulesText.length > 0 && (
        <Section title="Group rules">
          <ul className="space-y-1 text-[12px] text-fg-muted">
            {s.rulesText.map((r) => (
              <li key={r.id} className="flex items-start gap-1.5">
                <span className={cn('mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full', fired.has(r.id) ? 'bg-up' : 'bg-[var(--line-soft)]')} />
                <span>
                  {r.text}
                  {fired.has(r.id) && <span className="ml-1 text-[10.5px] text-fg-faint">fired</span>}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title={`Members · ${g.members.length}`}>
        <div className="space-y-1">
          {g.members.map((w, i) => {
            const m = s.members[i]
            const isOpen = open === w.id
            return (
              <div key={w.id} className="rounded-lg border border-[var(--line-soft)]">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : w.id)}
                  className="flex w-full items-center gap-2.5 px-2.5 py-2 text-left hover:bg-[var(--color-surface-2)]"
                >
                  <span className="num flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[var(--color-inset)] text-[10.5px] font-semibold text-fg-muted">
                    {m?.key ?? i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-medium text-fg">{w.name}</span>
                    <span className="num block truncate text-[11px] text-fg-subtle">
                      {w.summary.headline}
                      {m?.startText ? ` · ${m.startText.replace(/\.$/, '')}` : ''}
                    </span>
                  </span>
                  <span className="num shrink-0 text-[11.5px] text-down">{usd(-(w.summary.maxLossNet ?? w.summary.maxLoss))}</span>
                  {!draft && <StatusPill status={w.status} />}
                  <ChevronRight size={14} className={cn('shrink-0 text-fg-faint transition-transform', isOpen && 'rotate-90')} />
                </button>
                {isOpen && (
                  <div className="border-t border-[var(--separator)] pt-2.5">
                    <WorkerBody w={w} variant="chat" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </Section>

      {draft && (
        <Section title="Checks" right={blocking.length ? <span className="text-[10.5px] text-down">{blocking.length} blocking</span> : null}>
          {blocking.length ? (
            <ul className="space-y-0.5 text-[12px]">
              {blocking.map(({ w, c }) => (
                <li key={`${w.id}-${c.id}`} className="flex items-start gap-1.5">
                  <X size={12} className="mt-[3px] shrink-0 text-down" />
                  <span className="min-w-0">
                    <span className="text-down">
                      {w.name} — {c.label}
                    </span>
                    <span className="text-fg-faint"> — {c.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-1.5 text-[12px] text-fg-muted">
              <Check size={12} className="text-up" /> Every member passes its checks — open a member for the details.
            </div>
          )}
        </Section>
      )}

      {draft && s.warnings.length > 0 && (
        <Section title="Keep in mind">
          <ul className="list-disc space-y-0.5 pl-4 text-[11.5px] text-fg-subtle">
            {s.warnings.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </Section>
      )}

      {!draft && rt && rt.events.length > 0 && (
        <Section title="Group timeline">
          <ul className="space-y-0.5 text-[11.5px]">
            {[...rt.events]
              .reverse()
              .slice(0, 10)
              .map((e, i) => (
                <li key={i} className="flex gap-2">
                  <span className="num shrink-0 text-fg-faint">{when(e.at)}</span>
                  <span className="text-fg-muted">{e.message}</span>
                </li>
              ))}
          </ul>
        </Section>
      )}

      {g.error && <div className={cn('px-3.5 pb-2 text-[12px]', g.status === 'error' ? 'text-down' : 'text-fg-subtle')}>{g.error}</div>}
      {g.spec.note && (
        <Section title="Why (from the chat)">
          <p className="text-[11.5px] leading-relaxed text-fg-subtle">{g.spec.note}</p>
        </Section>
      )}
    </>
  )
}

export function WorkerGroupCard({ id, variant = 'chat' }: { id: string; variant?: 'chat' | 'page' }) {
  const { group: g, error, reload } = useWorkerGroup(id)
  const prefillComposer = useStore((s) => s.prefillComposer)
  const [armed, setArmed] = useState<null | 'approve' | 'close'>(null)
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'close'>(null)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(null), 6_000) // a confirmation never lingers
    return () => clearTimeout(t)
  }, [armed])
  useEffect(() => {
    if (msg?.tone !== 'ok') return
    const t = setTimeout(() => setMsg(null), 5_000)
    return () => clearTimeout(t)
  }, [msg])

  if (error && !g) return <div className="card mt-2 px-3.5 py-3 text-[12.5px] text-fg-subtle">Worker group {id}: {error}</div>
  if (!g) return <div className="card mt-2 h-32 animate-pulse" />

  const act = async (kind: NonNullable<typeof busy>, fn: () => Promise<unknown>, ok: string) => {
    setBusy(kind)
    setMsg(null)
    try {
      await fn()
      setMsg({ tone: 'ok', text: ok })
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Something went wrong' })
    } finally {
      setBusy(null)
      setArmed(null)
      void reload()
    }
  }
  const draft = g.status === 'draft'
  const running = g.status === 'active'
  const blocking = g.members.some((w) => w.checks.some((c) => !c.ok && c.level !== 'info'))
  const paper = g.members.every((w) => /^DU/i.test(w.account ?? ''))
  const orders = g.members.reduce((a, w) => a + w.plan.orders.length, 0)

  return (
    <div className={cn('card overflow-hidden', variant === 'chat' && 'mt-2')}>
      <div className="flex items-start justify-between gap-3 px-3.5 pb-2 pt-3">
        <div className="min-w-0">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">Worker group{paper ? ' · paper' : ''}</div>
          <div className="mt-0.5 truncate text-[14px] font-semibold text-fg">{g.name}</div>
          <div className="num mt-0.5 text-[12px] text-fg-muted">{g.summary.headline}</div>
        </div>
        <GroupPill status={g.status} />
      </div>

      <GroupBody g={g} />

      {(draft || running) && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--separator)] px-3.5 py-2.5">
          {draft && (
            <>
              <button
                type="button"
                disabled={!!busy || blocking}
                title={blocking ? 'A member’s check is blocking this group' : undefined}
                onClick={() =>
                  armed === 'approve'
                    ? void act('approve', () => workersApi.approveGroup(g.id, g.planHash), `Approved — ${g.members.length} workers sent to the broker.`)
                    : setArmed('approve')
                }
                className={cn(btn, 'bg-[var(--color-accent)] text-[var(--color-base)]')}
              >
                <Check size={13} />
                {busy === 'approve'
                  ? 'Placing orders…'
                  : armed === 'approve'
                    ? `Confirm — place ${g.members.length} workers (${orders} orders)`
                    : 'Approve group'}
              </button>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => prefillComposer(`Worker grubunu düzenle (${g.id}): `)}
                className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
              >
                <Pencil size={13} /> Edit
              </button>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void act('reject', () => workersApi.rejectGroup(g.id), 'Rejected — nothing was sent to the broker.')}
                className={cn(btn, 'text-fg-subtle hover:text-down')}
              >
                <X size={13} /> Reject
              </button>
              <span className="num ml-auto text-[10.5px] text-fg-faint" title="Approval is bound to exactly these members and links">
                group #{g.planHash.slice(0, 8)}
              </span>
            </>
          )}
          {running && (
            <button
              type="button"
              disabled={!!busy}
              onClick={() =>
                armed === 'close'
                  ? void act('close', () => workersApi.closeGroup(g.id), 'Stopping every member — cancelling orders and closing at market.')
                  : setArmed('close')
              }
              className={cn(btn, armed === 'close' ? 'bg-down text-white' : 'border border-[var(--line-soft)] text-fg-muted hover:text-down')}
            >
              <Power size={13} />
              {armed === 'close' ? 'Confirm — stop every member (cancel & close at market)' : 'Stop group'}
            </button>
          )}
        </div>
      )}
      {msg && <div className={cn('px-3.5 pb-2.5 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
    </div>
  )
}
